import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useAddChildNode } from 'api/decomposition-tree/hooks/useAddChildNode';
import { useMoveNode } from 'api/decomposition-tree/hooks/useMoveNode';
import { useStickyTopNodes } from 'api/decomposition-tree/hooks/useStickyTopNodes';
import { useTreeRoots } from 'api/decomposition-tree/hooks/useTreeRoots';
import { patchHasChildren } from 'api/decomposition-tree/utils/treeCacheLookups';
import { treeQueryKeys } from 'api/decomposition-tree/utils/treeQueryKeys';
import { useAddProjectNode } from 'api/project-nodes/hooks/useAddProjectNode';
import {
  useSelectedProjectTemplateNode,
  useSelectedProjectTemplateNodeDisplayNameAndIcon
} from 'state/selectedProject/selectedProjectHooks';
import { getDisplayName } from 'utils/names';
import { hasValue } from 'utils/values';

import { useRequestMove } from '../move/hooks/useRequestMove';
import { matchTreeBasePath, matchTreeNodeDetailsPath } from '../routes';
import { mergeStickyWithPaginated } from '../utils/mergeNodes';
import { isRealNode } from '../utils/treeId';
import { useInfiniteScrollSentinel } from './useInfiniteScrollSentinel';
import { useTreeDataLayer } from './useTreeDataLayer';
import { useTreeNodeCallbacks } from './useTreeNodeCallbacks';

type UseDecompositionTreePageArgs = {
  projectId: string;
  templateNodeId: string;
  proposalId?: string;
};

export function useDecompositionTreePage({ projectId, templateNodeId, proposalId }: UseDecompositionTreePageArgs) {
  const history = useHistory();
  const location = useLocation();

  const treeBaseMatch = useMemo(() => matchTreeBasePath(location.pathname), [location.pathname]);

  // The details route is the only source of truth for which node is selected: opening/closing
  // navigates the URL (see onOpenDetails / closeDetails below), and
  // selection here just reads that back — no separate state to keep in sync with it.
  const selectedDataNodeId = useMemo(
    () => matchTreeNodeDetailsPath(location.pathname)?.params.id ?? null,
    [location.pathname]
  );

  const queryClient = useQueryClient();
  const { icon, name } = useSelectedProjectTemplateNodeDisplayNameAndIcon(templateNodeId);
  const templateNode = useSelectedProjectTemplateNode(templateNodeId);
  const primaryProperty = templateNode?.properties.find((p) => p.id === templateNode?.primaryPropertyId);
  const titlePlaceholder = hasValue(primaryProperty) ? getDisplayName(primaryProperty.displayNames) : undefined;

  const {
    data: rootsData,
    isLoading,
    isPending,
    isError: isRootsError,
    hasNextPage: hasMoreRoots,
    isFetchingNextPage: isFetchingMoreRoots,
    fetchNextPage: fetchNextRootsPage
  } = useTreeRoots(projectId, templateNodeId, proposalId);

  const stickyRoots = useStickyTopNodes(projectId, templateNodeId, proposalId);

  const roots = useMemo(() => mergeStickyWithPaginated(stickyRoots, rootsData?.pages), [rootsData, stickyRoots]);

  const { mutate: addNodeMutation, isPending: isAddingNode } = useAddProjectNode(
    projectId,
    templateNodeId,
    proposalId,
    {
      technicalName: templateNode?.technicalName
    }
  );
  const { mutate: addChildNodeMutation } = useAddChildNode(projectId, templateNodeId, proposalId);
  const { mutateAsync: moveNodeMutation } = useMoveNode(projectId, templateNodeId, proposalId);

  const {
    openedParents,
    closeParent,
    closeAllParents,
    handleSnapshot,
    handleShowMore,
    loadingParentIds,
    treeItems,
    itemMetaMap,
    treeItemsRef,
    itemMetaMapRef,
    openParent,
    openParentIfNeeded
  } = useTreeDataLayer({ roots, titlePlaceholder });

  const [mainExpandedItems, setMainExpandedItems] = useState<string[]>([]);

  const fetchNextRoots = useCallback(() => {
    void fetchNextRootsPage();
  }, [fetchNextRootsPage]);

  const { moveTarget, requestMove, closeMoveDialog } = useRequestMove({
    treeItemsRef,
    itemMetaMapRef
  });

  const closeDetails = useCallback(() => {
    if (treeBaseMatch) history.push(treeBaseMatch.url);
  }, [history, treeBaseMatch]);
  // Replace, so Back can't return to a details URL that would only close itself again.
  const dropUnavailableDetails = useCallback(() => {
    if (treeBaseMatch) history.replace(treeBaseMatch.url);
  }, [history, treeBaseMatch]);

  const callbacks = useTreeNodeCallbacks({
    resolveDataNodeId: (treeId) => itemMetaMap[treeId]?.dataNodeId ?? treeId,
    onOpenDetails: (dataNodeId) => {
      if (treeBaseMatch?.url) {
        history.push(`${treeBaseMatch.url}/d/${dataNodeId}/details`);
      } else {
        console.warn('DecompositionTreePage: current pathname did not match a tree base route', location.pathname);
      }
    },
    onRequestMove: requestMove,
    onAddChild: (parentTreeId, parentDataNodeId) => {
      addChildNodeMutation(
        { endDataNodeId: parentDataNodeId },
        {
          onSuccess: ({ createdNodeId }) => {
            // Everything below is initiator-only: it reflects the add on current screen
            // Skip it all when nothing was created, otherwise we'd leave a phantom expand arrow.
            if (createdNodeId) {
              patchHasChildren(queryClient, { projectId, templateNodeId, proposalId }, parentDataNodeId);
              setMainExpandedItems((prev) => (prev.includes(parentTreeId) ? prev : [...prev, parentTreeId]));
              openParent(parentTreeId, parentDataNodeId);
            }
          }
        }
      );
    },
    onShowMore: handleShowMore
  });

  const handleExpansionChange = useCallback(
    (_: React.SyntheticEvent | null, next: string[]) => {
      const prevSet = new Set(mainExpandedItems);
      const nextSet = new Set(next);

      next.filter((id) => isRealNode(id) && !prevSet.has(id)).forEach(openParentIfNeeded);
      mainExpandedItems.filter((id) => isRealNode(id) && !nextSet.has(id)).forEach(closeParent);

      setMainExpandedItems(next);
    },
    [mainExpandedItems, closeParent, openParentIfNeeded]
  );

  const handleCollapseAll = useCallback(() => {
    setMainExpandedItems([]);
    closeAllParents();
  }, [closeAllParents]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useInfiniteScrollSentinel({
    sentinelRef,
    hasMore: !!hasMoreRoots,
    isFetching: isFetchingMoreRoots,
    isError: isRootsError,
    fetchNextPage: fetchNextRoots
  });

  useEffect(() => {
    return () => {
      closeAllParents();
      // Drop the whole project tree cache on leave. Sticky-top pins are not server-backed
      // (`enabled: false`); leaving them around would resurrect "just added" nodes on the next visit.
      queryClient.removeQueries({ predicate: treeQueryKeys.inProject(projectId) });
    };
  }, [projectId, queryClient, closeAllParents]);

  return {
    icon,
    name,
    titlePlaceholder,
    isLoading,
    isPending,
    hasMoreRoots,
    isFetchingMoreRoots,
    fetchNextRoots,
    roots,
    treeItems,
    itemMetaMap,
    loadingParentIds,
    openedParents,
    handleSnapshot,
    mainExpandedItems,
    handleExpansionChange,
    handleCollapseAll,
    callbacks,
    sentinelRef,
    moveTarget,
    closeMoveDialog,
    moveNodeMutation,
    addNodeMutation,
    isAddingNode,
    selectedDataNodeId,
    closeDetails,
    dropUnavailableDetails,
    templateNode
  };
}
